import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtMoney, fmtDate, todayIso, CURRENCY_OPTIONS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import FileUploader from "@/components/FileUploader";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
} from "recharts";
import { Plus, Trash2, Wallet, Fuel, ShoppingBasket, Receipt } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#0B3676","#16A34A","#F59E0B","#DC2626","#8B5CF6","#06B6D4","#EC4899","#84CC16",
  "#F97316","#14B8A6","#A855F7","#EAB308","#0EA5E9","#EF4444","#22C55E","#6366F1"];

const PAYMENT_METHODS = {
  cash: "Nakit", card: "Kart", transfer: "Havale/EFT",
  check: "Çek", other: "Diğer",
};

export default function Expenses() {
  const { user } = useAuth();
  const [tab, setTab] = useState("list");
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterEmp, setFilterEmp] = useState("all");

  const [form, setForm] = useState({
    category: "", amount: "", currency: "TRY", expense_date: todayIso(),
    description: "", vendor: "", employee_id: "", employee_name: "",
    payment_method: "cash", receipt_url: "", receipts: [],
  });

  const loadAll = async () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (filterCat !== "all") params.category = filterCat;
    if (filterEmp !== "all") params.employee_id = filterEmp;
    const [c, e, s] = await Promise.all([
      api.get("/expense-categories"),
      api.get("/expenses", { params }),
      api.get("/expenses/summary", { params: { date_from: dateFrom, date_to: dateTo } }),
    ]);
    setCategories(c.data);
    setExpenses(e.data);
    setSummary(s.data);
  };
  useEffect(() => { loadAll(); }, [dateFrom, dateTo, filterCat, filterEmp]);
  useEffect(() => {
    // Personnel list — accessible to admin/accounting; sales/warehouse/viewer will get empty
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.amount) {
      toast.error("Kategori ve tutar zorunlu");
      return;
    }
    try {
      const payload = {
        category: form.category,
        amount: Number(form.amount),
        currency: form.currency,
        expense_date: form.expense_date,
        description: form.description,
        vendor: form.vendor,
        employee_id: form.employee_id || null,
        employee_name: form.employee_id ? "" : form.employee_name,
        payment_method: form.payment_method,
        receipt_url: (form.receipts || [])[0]?.download_url || "",
      };
      await api.post("/expenses", payload);
      toast.success("Gider kaydedildi");
      setForm({
        category: "", amount: "", currency: "TRY", expense_date: todayIso(),
        description: "", vendor: "", employee_id: "", employee_name: "",
        payment_method: "cash", receipt_url: "", receipts: [],
      });
      setTab("list");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu gideri silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Silindi");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const TabBtn = ({ id, label, icon: Icon }) => (
    <button onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              tab === id ? "bg-[#0B3676] text-white" : "bg-white text-[#6B7280] border border-[#E5E7EB] hover:border-[#0B3676]"
            }`}>
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <TabBtn id="new" label="Gider Ekle" icon={Plus} />
        <TabBtn id="list" label="Gider Listesi" icon={Receipt} />
        <TabBtn id="report" label="Gider Raporu" icon={Wallet} />
      </div>

      {tab === "new" && (
        <form onSubmit={submit} className="azd-card p-6 max-w-4xl space-y-4">
          <div className="text-sm font-bold text-[#08285A] uppercase tracking-wider">Yeni Gider</div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1.5">Kategori <span className="text-[#DC2626]">*</span></label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="exp-category"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Tarih <span className="text-[#DC2626]">*</span></label>
              <Input type="date" value={form.expense_date}
                     onChange={(e) => set("expense_date", e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Tutar <span className="text-[#DC2626]">*</span></label>
              <Input type="number" min="0.01" step="0.01" value={form.amount}
                     onChange={(e) => set("amount", e.target.value)} required data-testid="exp-amount" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Para Birimi</label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Ödeme Yöntemi</label>
              <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Tedarikçi / Alınan Yer</label>
              <Input value={form.vendor} onChange={(e) => set("vendor", e.target.value)}
                     placeholder="Örn: Shell Petrol, A101, Restoran adı" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Harcayan Personel</label>
              <Select value={form.employee_id || "manual"}
                      onValueChange={(v) => set("employee_id", v === "manual" ? "" : v)}>
                <SelectTrigger data-testid="exp-employee">
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">— Manuel Ad Yaz —</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!form.employee_id && (
                <Input className="mt-2" value={form.employee_name}
                       onChange={(e) => set("employee_name", e.target.value)}
                       placeholder={`Manuel isim (varsayılan: ${user?.full_name})`}
                       data-testid="exp-employee-manual" />
              )}
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold mb-1.5">Açıklama</label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                        rows={2} placeholder="Ne için harcandı? Detay yazabilirsiniz." />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold mb-1.5">Fiş / Fatura Görseli (opsiyonel)</label>
              <FileUploader value={form.receipts || []}
                            onChange={(v) => set("receipts", v)}
                            category="expenses"
                            accept="image/jpeg,image/png,image/webp,application/pdf"
                            maxFiles={1}
                            label="Fiş Yükle" testid="exp-receipt" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E7EB]">
            <Button type="button" variant="outline" onClick={() => setTab("list")}>Vazgeç</Button>
            <Button type="submit" data-testid="exp-save"
                    className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              Gideri Kaydet
            </Button>
          </div>
        </form>
      )}

      {tab === "list" && (
        <>
          <div className="azd-card p-4 grid lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5">Başlangıç</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Bitiş</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Kategori</label>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Personel</label>
              <Select value={filterEmp} onValueChange={setFilterEmp}>
                <SelectTrigger data-testid="filter-employee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="w-full text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam (TL)</div>
                <div className="text-2xl font-extrabold text-[#0B3676]">
                  {fmtMoney(summary?.total || 0)}
                </div>
              </div>
            </div>
          </div>

          <div className="azd-card overflow-hidden">
            <div className="overflow-x-auto azd-scrollbar">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                    <th className="text-left px-4 py-3 font-semibold">Kategori</th>
                    <th className="text-left px-4 py-3 font-semibold">Harcayan</th>
                    <th className="text-left px-4 py-3 font-semibold">Tedarikçi</th>
                    <th className="text-left px-4 py-3 font-semibold">Açıklama</th>
                    <th className="text-right px-4 py-3 font-semibold">Tutar</th>
                    <th className="text-center px-4 py-3 font-semibold">Onay</th>
                    <th className="text-right px-4 py-3 font-semibold">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    <tr><td colSpan={8} className="p-8 text-center text-[#6B7280]">Gider yok</td></tr>
                  ) : expenses.map((e) => {
                    const st = e.approval_status || "pending";
                    const stBadge = {
                      pending: { label: "Beklemede", variant: "warning" },
                      approved: { label: "Onaylandı", variant: "success" },
                      rejected: { label: "Reddedildi", variant: "danger" },
                    }[st] || { label: st, variant: "default" };
                    const canApprove = ["admin", "accounting"].includes(user?.role) && st === "pending";
                    return (
                    <tr key={e.id} className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50">
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{fmtDate(e.expense_date)}</td>
                      <td className="px-4 py-3"><Badge variant="info">{e.category}</Badge></td>
                      <td className="px-4 py-3 text-sm">{e.employee_name || e.created_by_name}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{e.vendor || "-"}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] max-w-xs truncate" title={e.description}>
                        {e.description || "-"}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#DC2626]">
                        {fmtMoney(e.amount, e.currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={stBadge.variant}>{stBadge.label}</Badge>
                        {e.approved_by_name && (
                          <div className="text-[10px] text-[#6B7280] mt-0.5">
                            {e.approved_by_name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canApprove && (
                          <>
                            <button onClick={async () => {
                              try { await api.post(`/expenses/${e.id}/approve`); toast.success("Onaylandı"); loadAll(); }
                              catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
                            }} data-testid={`approve-expense-${e.id}`}
                                    className="p-1.5 text-[#16A34A] hover:bg-[#16A34A]/10 rounded"
                                    title="Onayla">✓</button>
                            <button onClick={async () => {
                              try { await api.post(`/expenses/${e.id}/reject`); toast.success("Reddedildi"); loadAll(); }
                              catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
                            }} data-testid={`reject-expense-${e.id}`}
                                    className="p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                                    title="Reddet">✗</button>
                          </>
                        )}
                        <button onClick={() => remove(e.id)}
                                data-testid={`delete-expense-${e.id}`}
                                className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "report" && summary && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="azd-card azd-stat-card azd-error p-5">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Toplam Gider</div>
              <div className="text-3xl font-extrabold text-[#DC2626] mt-1">{fmtMoney(summary.total)}</div>
            </div>
            <div className="azd-card azd-stat-card p-5">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Gider Sayısı</div>
              <div className="text-3xl font-extrabold text-[#08285A] mt-1">{summary.count}</div>
            </div>
            <div className="azd-card azd-stat-card azd-accent p-5">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">Kategori Sayısı</div>
              <div className="text-3xl font-extrabold text-[#08285A] mt-1">{summary.by_category.length}</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="azd-card p-5">
              <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
                Kategoriye Göre Dağılım
              </div>
              <div className="h-80">
                {summary.by_category.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-[#6B7280] text-sm">Veri yok</div>
                ) : (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={summary.by_category} dataKey="value" nameKey="name" outerRadius={110}
                           label={(e) => `%${((e.value / summary.total) * 100).toFixed(0)}`}>
                        {summary.by_category.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtMoney(v)} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="azd-card p-5">
              <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
                Aylık Gider Trendi
              </div>
              <div className="h-80">
                {summary.by_month.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-[#6B7280] text-sm">Veri yok</div>
                ) : (
                  <ResponsiveContainer>
                    <BarChart data={summary.by_month}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                      <Tooltip formatter={(v) => fmtMoney(v)} />
                      <Bar dataKey="value" fill="#DC2626" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {summary.by_employee && summary.by_employee.length > 0 && (
            <div className="azd-card p-5" data-testid="employee-breakdown">
              <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
                Personele Göre Harcamalar
              </div>
              <div className="h-80">
                <ResponsiveContainer>
                  <BarChart data={summary.by_employee} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" tick={{ fontSize: 11 }}
                           tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                    <Bar dataKey="value" fill="#0B3676" radius={[0, 6, 6, 0]}>
                      {summary.by_employee.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="azd-card p-5">
            <div className="text-sm font-bold text-[#08285A] mb-3 uppercase tracking-wider">
              Kategori Bazlı Detay
            </div>
            <div className="space-y-2">
              {summary.by_category.map((c, i) => {
                const pct = summary.total > 0 ? (c.value / summary.total) * 100 : 0;
                return (
                  <div key={c.name}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-semibold text-[#111827]">{c.name}</span>
                      <span className="font-bold text-[#DC2626]">
                        {fmtMoney(c.value)} <span className="text-[#6B7280] text-xs">(%{pct.toFixed(1)})</span>
                      </span>
                    </div>
                    <div className="h-2 bg-[#F6F8FB] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all"
                           style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
